import { useState, useRef, useMemo } from 'react'
import toast from 'react-hot-toast';
import { PageLoader } from '../../components/common/Loader';
import { useParams, useNavigate } from 'react-router-dom'
import Breadcrumb from '../../components/common/Breadcrumb'
import { useAuth } from '../../contexts/AuthContext'
import {
    IconCheck, IconBan, IconChevronLeft, IconClock, IconBeach, IconCalendarMonth, IconSignature,
    IconCircleCheck, IconAlertTriangle, IconX
} from '@tabler/icons-react'
import {
    useTimesheet,
    useApproveTimesheet,
    useHrApproveTimesheet,
    useRejectTimesheet,
    useHolidays,
    useMyApprovedLeaves
} from '../../hooks/useTimesheets';
import { useEmployees } from '../../hooks/useEmployees';
import { months } from '../../data/timesheetData';

const AVATAR_COLORS = ['#4f46e5', '#0891b2', '#059669', '#d97706', '#dc2626', '#7c3aed', '#db2777'];

export default function TimesheetApprovalDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, hasAnyPermission } = useAuth();
    const orgId = user?.orgId;

    // Primary Timesheet Data ---
    const { data: timesheetData, isLoading: isTimesheetLoading } = useTimesheet(id, orgId);

    const subordinateEmployeeId = timesheetData?.employee_id || timesheetData?.employee?.id || timesheetData?.employee;
    const isSelf = !!subordinateEmployeeId && String(subordinateEmployeeId) === String(user?.employeeId);
    const tsYear = timesheetData?.year;
    const tsMonth = timesheetData?.month;

    // Supplemental Data (Holidays, Leaves & Employees) ---
    const { data: employeesData } = useEmployees(orgId);
    const employeeRecord = useMemo(() => {
        const list = Array.isArray(employeesData) ? employeesData : (employeesData?.results || []);
        return list.find(e => e.id === subordinateEmployeeId);
    }, [employeesData, subordinateEmployeeId]);

    const { data: holidaysData } = useHolidays(orgId, subordinateEmployeeId, { waitForEmployeeId: true });
    // Pass tsYear and tsMonth so the query actually fires (enabled requires year != null && month != null)
    const { data: approvedLeavesData } = useMyApprovedLeaves(orgId, tsYear, tsMonth, subordinateEmployeeId);

    // the user role 
    const isHR = user?.is_superuser || hasAnyPermission(['rbacapp.hr_act', 'rbacapp.hr_approve', 'rbacapp.hr_view']);

    const managerApprove = useApproveTimesheet();
    const hrApprove = useHrApproveTimesheet();
    const rejectTimesheet = useRejectTimesheet();

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const leftPaneRef = useRef(null);
    const rightPaneRef = useRef(null);

    const isApproving = managerApprove.isPending || hrApprove.isPending;

    const formatDateLocal = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    const isWeekend = (date) => {
        const day = date.getDay();
        return day === 0 || day === 6;
    };

    const getMonthName = (month) => {
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
        return monthNames[month - 1] || '';
    };

    // Lookups ---
    const holidayLookup = useMemo(() => {
        const lookup = {};
        const holidays = Array.isArray(holidaysData) ? holidaysData : [];
        holidays.forEach(h => {
            if (h.date) {
                lookup[h.date] = {
                    name: h.name || 'Holiday',
                    hours: parseFloat(h.hours) || 8
                };
            }
        });
        return lookup;
    }, [holidaysData]);

    const leaveLookup = useMemo(() => {
        const lookup = {};
        if (!approvedLeavesData) return lookup;
        const leaveDates = approvedLeavesData.leave_dates || [];
        leaveDates.forEach(dateStr => {
            if (dateStr) {
                const date = new Date(dateStr);
                const dayOfWeek = date.getDay();
                if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                    lookup[dateStr] = { hours: 8 };
                }
            }
        });
        return lookup;
    }, [approvedLeavesData]);

    // Derived Data ---
    const employeeName = timesheetData?.employee_name || timesheetData?.employee?.name || 'Employee';
    const timesheetStatus = timesheetData?.status || 'Submitted';
    const signedAt = timesheetData?.signed_at || timesheetData?.submitted_at;
    const daysInMonth = tsYear && tsMonth ? new Date(tsYear, tsMonth, 0).getDate() : 0;
    const periodDays = Array.from({ length: daysInMonth }, (_, i) => new Date(tsYear, tsMonth - 1, i + 1));

    const timeEntries = useMemo(() => {
        const lines = timesheetData?.lines || [];
        return lines.map(line => ({
            id: line.id,
            description: line.description || '',
            project: line.project?.name || 'No Project',
            isLeave: line.is_leave || false,
            isTimeOff: line.is_time_off || false,
            hours: line.entries ? Object.entries(line.entries).reduce((acc, [dateStr, val]) => {
                const d = parseInt(dateStr.split('-')[2], 10);
                acc[d - 1] = typeof val === 'object' ? parseFloat(val.hours) : parseFloat(val);
                return acc;
            }, {}) : {}
        }));
    }, [timesheetData?.lines]);

    // Totals ---
    const calculateLeaveHoursForFirstRow = () => {
        let leaveTotal = 0;
        periodDays.forEach((date) => {
            const ds = formatDateLocal(date);
            const leave = leaveLookup[ds];
            const weekend = isWeekend(date);
            if (leave && !weekend) {
                leaveTotal += leave.hours;
            }
        });
        return leaveTotal;
    };

    const calculateHolidayHours = () => {
        return periodDays.reduce((sum, date) => {
            const ds = formatDateLocal(date);
            return sum + (holidayLookup[ds]?.hours || 0);
        }, 0);
    };

    const calculateLeaveHours = () => {
        return periodDays.reduce((sum, date) => {
            const ds = formatDateLocal(date);
            const weekend = isWeekend(date);
            if (!weekend && leaveLookup[ds]) {
                return sum + (leaveLookup[ds]?.hours || 0);
            }
            return sum;
        }, 0);
    };

    const calculateProjectHours = () => {
        let total = 0;
        timeEntries.forEach(entry => {
            periodDays.forEach((date, dayIdx) => {
                const ds = formatDateLocal(date);
                const isLeaveDay = !!leaveLookup[ds];
                const weekend = isWeekend(date);
                if (!isLeaveDay && !weekend && entry.hours[dayIdx]) {
                    total += entry.hours[dayIdx];
                }
            });
        });
        return total;
    };

    const calculateRowTotalExcludingLeave = (entryHours) => {
        let total = 0;
        periodDays.forEach((date, dayIdx) => {
            const ds = formatDateLocal(date);
            const isLeaveDay = !!leaveLookup[ds];
            const weekend = isWeekend(date);
            if (!isLeaveDay && !weekend && entryHours[dayIdx]) {
                total += entryHours[dayIdx];
            }
        });
        return total;
    };

    const calculateGrandTotal = () => {
        return calculateProjectHours() + calculateHolidayHours();
    };

    const handleConfirmApprove = () => {
        const approveHook = isHR ? hrApprove : managerApprove;
        approveHook.mutate({ id, orgId }, {
            onSuccess: () => { setShowApproveModal(false); navigate('/approvals/timesheet'); }
        });
    };

    const handleConfirmReject = () => {
        if (!rejectReason.trim()) return toast.error('Reason required');
        rejectTimesheet.mutate({ id, reason: rejectReason, orgId }, {
            onSuccess: () => { setShowRejectModal(false); navigate('/approvals/timesheet'); }
        });
    };

    const handleVerticalScroll = (e) => {
        const scrollTop = e.target.scrollTop;
        if (leftPaneRef.current && e.target !== leftPaneRef.current) leftPaneRef.current.scrollTop = scrollTop;
        if (rightPaneRef.current && e.target !== rightPaneRef.current) rightPaneRef.current.scrollTop = scrollTop;
    };

    const getStatusColor = () => {
        const status = timesheetStatus?.toLowerCase();
        if (status === 'signed' || status === 'submitted') return 'text-org-blue-primary bg-org-blue-primary/10';
        if (status === 'approved') return 'text-org-green-primary bg-org-green-primary/10';
        if (status === 'rejected') return 'text-red-600 bg-red-50';
        return 'text-gray-600 bg-gray-50';
    };

    const approverName = user?.first_name ? `${user.first_name} ${user.last_name}` : user?.username || 'You';
    const employeeInitials = employeeName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';
    const avatarBg = AVATAR_COLORS[(employeeName?.charCodeAt(0) || 0) % AVATAR_COLORS.length];

    if (isTimesheetLoading) return <PageLoader />;

    return (
        <div className="flex flex-col min-h-screen gap-20 font-sans text-text-primary">
            <Breadcrumb title="Timesheet Approval" path="Approvals / Timesheets / Review" />

            {/* Back button */}
            <button
                onClick={() => navigate('/approvals/timesheet')}
                className="btn btn-outline-secondary flex items-center gap-5 mb-15 self-start"
            >
                <IconChevronLeft size={16} />
                Back
            </button>

            <div className='grid grid-cols-12 gap-20'>
                {/* 1. TOP INFO CARD */}
                <div className='col-span-12 bg-white rounded border border-border shadow-sm p-20'>
                    <div className='flex items-center justify-between mb-20'>
                        <div className='flex items-center gap-10'>
                            <div className='flex items-center gap-10 bg-gradient-to-r from-org-blue-primary/10 to-org-green-primary/10 rounded-lg px-20 py-12 border border-org-blue-primary/20'>
                                <IconCalendarMonth size={22} className='text-org-blue-primary' />
                                <div className='text-left'>
                                    <p className='text-[9px] text-org-blue-primary font-semibold uppercase tracking-wide'>Review Period</p>
                                    <p className='text-base font-bold text-gray-800'>{months[tsMonth - 1]} {tsYear}</p>
                                </div>
                            </div>
                        </div>

                        <div className='flex items-center gap-6'>
                            {isSelf ? (
                                <span className='text-xs text-gray-400 italic px-10 py-4'>You cannot approve your own timesheet</span>
                            ) : (
                                <>
                                    <button
                                        onClick={() => setShowRejectModal(true)}
                                        className='flex items-center gap-2 bg-red-50 text-red-600 border border-red-200 px-10 py-4 rounded text-xs font-semibold hover:bg-red-100'
                                    >
                                        <IconBan size={14} />Reject
                                    </button>
                                    <button
                                        onClick={() => setShowApproveModal(true)}
                                        className='flex items-center gap-2 bg-primary text-white px-10 py-4 rounded text-xs font-semibold hover:opacity-90'
                                    >
                                        <IconCheck size={14} />{isHR ? 'HR Approve' : 'Approve'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    <div className='pt-10 flex items-center justify-between'>
                        <div className='flex items-center gap-40'>
                            <h2 className='text-lg font-bold text-gray-800 pr-20 border-r border-border'>Timesheet Review</h2>
                            <div className='flex gap-40 text-xs'>
                                <div>
                                    <p className='text-gray-500 mb-2 uppercase text-[9px] font-bold tracking-wider'>Employee</p>
                                    <p className='font-semibold text-sm whitespace-nowrap'>{employeeName}</p>
                                </div>
                                <div>
                                    <p className='text-gray-500 mb-2 uppercase text-[9px] font-bold tracking-wider'>Employee No</p>
                                    <p className='font-semibold text-sm'>{employeeRecord?.employee_no || 'N/A'}</p>
                                </div>
                                <div>
                                    <p className='text-gray-500 mb-2 uppercase text-[9px] font-bold tracking-wider'>Status</p>
                                    <span className={`font-bold text-sm uppercase px-8 py-2 rounded ${getStatusColor()}`}>
                                        {timesheetStatus}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {signedAt && (
                        <div className='mt-15 pt-15 border-t border-border flex items-center gap-20 text-xs'>
                            <div className='flex items-center gap-6 text-org-blue-primary'>
                                <IconSignature size={14} />
                                <span>Submitted: {new Date(signedAt).toLocaleString()}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* 2. SUMMARY CARDS */}
                <div className="col-span-12 grid grid-cols-3 gap-20">
                    <div className="bg-white p-15 rounded border border-border shadow-sm flex items-center gap-15">
                        <div className="w-40 h-40 rounded bg-org-blue-primary/10 flex items-center justify-center text-org-blue-primary font-bold">
                            <IconClock size={20} />
                        </div>
                        <div>
                            <p className="text-[10px] uppercase text-gray-500 font-bold tracking-tight">Total Hours</p>
                            <p className="text-lg font-bold text-org-blue-primary">{calculateGrandTotal().toFixed(1)}</p>
                        </div>
                    </div>
                    <div className="bg-white p-15 rounded border border-border shadow-sm flex items-center gap-15">
                        <div className="w-40 h-40 rounded bg-org-green-primary/10 flex items-center justify-center text-org-green-primary font-bold">
                            <IconBeach size={20} />
                        </div>
                        <div>
                            <p className="text-[10px] uppercase text-gray-500 font-bold tracking-tight">Holiday Hours</p>
                            <p className="text-lg font-bold text-org-green-primary">{calculateHolidayHours().toFixed(1)}</p>
                        </div>
                    </div>
                    <div className="bg-white p-15 rounded border border-border shadow-sm flex items-center gap-15">
                        <div className="w-40 h-40 rounded bg-red-100 flex items-center justify-center text-red-600 font-bold">
                            <IconCalendarMonth size={20} />
                        </div>
                        <div>
                            <p className="text-[10px] uppercase text-gray-500 font-bold tracking-tight">Leave Hours</p>
                            <p className="text-lg font-bold text-red-600">{calculateLeaveHours().toFixed(1)}</p>
                        </div>
                    </div>
                </div>

                {/* 3. TABLE CARD */}
                <div className='col-span-12 bg-white p-20 rounded border border-border shadow-sm flex flex-col'>
                    <div className='flex items-center justify-between mb-15'>
                        <h3 className='text-sm font-semibold text-gray-700'>Timesheet Lines</h3>
                    </div>

                    <div className="flex border border-gray-300 rounded overflow-hidden">
                        {/* LEFT SECTION */}
                        <div className="w-[260px] flex-shrink-0 border-r-2 border-gray-400 flex flex-col">
                            <div className="overflow-y-auto scrollbar-hide max-h-[450px]" ref={leftPaneRef} onScroll={handleVerticalScroll}>
                                {/* Header */}
                                <div className="h-[56px] bg-org-blue-primary text-white flex items-center text-[10px] font-bold uppercase sticky top-0 z-10">
                                    <div className="w-[36px] px-4 border-r border-white/20 text-center flex-shrink-0">#</div>
                                    <div className="flex-grow px-8">Project / Description</div>
                                </div>

                                {/* Project Rows */}
                                {timeEntries.map((entry, idx) => (
                                    <div key={entry.id} className="h-[48px] border-b border-border flex items-center text-sm">
                                        <div className="w-[36px] px-4 border-r h-full flex items-center justify-center bg-gray-50 text-gray-400 font-bold text-[11px] flex-shrink-0">{idx + 1}</div>
                                        <div className="flex-grow px-8 h-full flex items-center overflow-hidden">
                                            <span className="text-xs font-medium truncate">{entry.description || entry.project}</span>
                                        </div>
                                    </div>
                                ))}

                            </div>
                        </div>

                        {/* RIGHT SECTION - Date columns */}
                        <div className="flex-grow flex flex-col overflow-hidden">
                            <div className="overflow-x-auto overflow-y-auto scrollbar-hide max-h-[450px]" ref={rightPaneRef} onScroll={handleVerticalScroll}>
                                <div className="flex">
                                    {periodDays.map((date, dayIdx) => {
                                        const dateStr = formatDateLocal(date);
                                        const holiday = holidayLookup[dateStr];
                                        const leave = leaveLookup[dateStr];
                                        const weekend = isWeekend(date);

                                        const getColumnBg = () => {
                                            if (holiday) return 'bg-org-green-primary/5';
                                            if (leave) return 'bg-red-50';
                                            if (weekend) return 'bg-gray-50';
                                            return '';
                                        };

                                        const getHeaderBgColor = () => {
                                            if (holiday) return '#6CBC50';
                                            if (leave) return '#dc2626';
                                            if (weekend) return '#9ca3af';
                                            return '#012E81';
                                        };

                                        return (
                                            <div key={dayIdx} className={`min-w-[85px] border-r border-border ${getColumnBg()}`}>
                                                {/* Header */}
                                                <div
                                                    className="h-[56px] text-white flex flex-col items-center justify-center text-[10px] font-medium sticky top-0 z-10 border-r border-white/20 relative overflow-hidden"
                                                    style={{ backgroundColor: getHeaderBgColor() }}
                                                >
                                                    <span className="font-bold">{date.toLocaleDateString('en-GB', { weekday: 'short' })}</span>
                                                    <span className="opacity-80 text-[9px]">{date.getDate()}</span>

                                                    {(holiday || leave) && !weekend && (
                                                        <div className="absolute bottom-1 left-1/2 -translate-x-1/2">
                                                            <span className="bg-white/30 backdrop-blur-sm px-2 py-0.5 rounded text-[6px] font-bold uppercase tracking-wide whitespace-nowrap">
                                                                {holiday ? 'Holiday' : 'Leave'}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Project Entry Rows */}
                                                {timeEntries.map((entry, entryIdx) => {
                                                    const entryHours = entry.hours[dayIdx];
                                                    const isLeaveDay = !!leave && !weekend;
                                                    const isProjectLine = !entry.isLeave && !entry.isTimeOff;
                                                    const isFirstProjectLine = entryIdx === 0 && isProjectLine;

                                                    let displayHours = null;
                                                    if (isLeaveDay) {
                                                        if (isFirstProjectLine) {
                                                            displayHours = leave.hours;
                                                        }
                                                    } else {
                                                        displayHours = entryHours || null;
                                                    }

                                                    const showLeaveHours = isLeaveDay && isFirstProjectLine;

                                                    return (
                                                        <div
                                                            key={entry.id}
                                                            className={`h-[48px] border-b border-border flex items-center justify-center text-sm ${showLeaveHours ? 'bg-red-100' : ''}`}
                                                        >
                                                            <span className={`font-medium ${showLeaveHours ? 'text-red-600' : 'text-gray-800'}`}>
                                                                {displayHours ? displayHours.toFixed(1) : ''}
                                                            </span>
                                                        </div>
                                                    );
                                                })}

                                            </div>
                                        );
                                    })}

                                    {/* TOTAL COLUMN */}
                                    <div className="min-w-[80px] bg-gray-100 sticky right-0 z-20 border-l-2 border-gray-400">
                                        <div className="h-[56px] bg-org-blue-primary text-white flex items-center justify-center text-[11px] font-bold sticky top-0 z-30">
                                            TOTAL
                                        </div>
                                        {timeEntries.map((entry, entryIdx) => {
                                            const projectTotal = calculateRowTotalExcludingLeave(entry.hours);
                                            const leaveHours = entryIdx === 0 ? calculateLeaveHoursForFirstRow() : 0;
                                            const total = projectTotal + leaveHours;
                                            return (
                                                <div key={entry.id} className="h-[48px] border-b border-border flex items-center justify-center text-sm font-bold bg-gray-50">
                                                    {total.toFixed(1)}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Approve Modal */}
            {showApproveModal && (
                <>
                    <div className='fixed inset-0 z-[100] flex items-center justify-center p-20'>
                        <div className='w-full max-w-[450px] bg-white rounded-xl shadow-2xl overflow-hidden'>
                            {/* Header */}
                            <div className='bg-primary px-20 py-15 flex items-center justify-between'>
                                <h3 className='text-lg font-semibold text-white flex items-center gap-8'>
                                    <IconCircleCheck size={20} />
                                    {isHR ? 'HR Approval' : 'Approve Timesheet'}
                                </h3>
                                <button
                                    onClick={() => setShowApproveModal(false)}
                                    disabled={isApproving}
                                    className='p-4 hover:bg-white/20 rounded-full disabled:opacity-50'
                                >
                                    <IconX size={20} className='text-white' />
                                </button>
                            </div>

                            {/* Body */}
                            <div className='p-20'>
                                {/* Employee info */}
                                <div className='bg-gray-50 border border-border rounded-lg p-15 mb-20'>
                                    <div className='flex items-center gap-12 mb-10'>
                                        <div
                                            style={{ backgroundColor: avatarBg }}
                                            className='w-[40px] h-[40px] rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0'
                                        >
                                            {employeeInitials}
                                        </div>
                                        <div>
                                            <p className='font-semibold text-gray-800'>{employeeName}</p>
                                        </div>
                                    </div>
                                    <div className='flex gap-20 text-sm text-gray-600'>
                                        <span><span className='font-medium'>Period:</span> {getMonthName(tsMonth)} {tsYear}</span>
                                        <span><span className='font-medium'>Hours:</span> {timesheetData?.total_hours || calculateGrandTotal().toFixed(1)}h</span>
                                    </div>
                                </div>

                                {/* Checklist */}
                                <p className='text-sm text-gray-600 mb-10'>By approving, you confirm that:</p>
                                <ul className='text-xs text-gray-500 space-y-6 mb-20'>
                                    <li className='flex items-start gap-8'>
                                        <IconCheck size={14} className='text-success mt-1 flex-shrink-0' />
                                        The reported hours have been reviewed and are accurate
                                    </li>
                                    <li className='flex items-start gap-8'>
                                        <IconCheck size={14} className='text-success mt-1 flex-shrink-0' />
                                        Time has been correctly charged to the relevant projects
                                    </li>
                                    {isHR && (
                                        <li className='flex items-start gap-8'>
                                            <IconCheck size={14} className='text-success mt-1 flex-shrink-0' />
                                            Line manager confirmation has been received
                                        </li>
                                    )}
                                </ul>

                                {/* Approver identity */}
                                <div className='flex items-center gap-15 p-15 bg-primary/10 rounded-lg'>
                                    <div className='w-[40px] h-[40px] rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0'>
                                        <IconCircleCheck size={22} className='text-primary' />
                                    </div>
                                    <div>
                                        <p className='font-semibold text-gray-800'>{approverName}</p>
                                        <p className='text-xs text-gray-500'>{new Date().toLocaleString()}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className='px-20 py-15 border-t border-border flex justify-end gap-10'>
                                <button
                                    onClick={() => setShowApproveModal(false)}
                                    disabled={isApproving}
                                    className='btn btn-outline-secondary disabled:opacity-50'
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmApprove}
                                    disabled={isApproving}
                                    className='btn btn-primary disabled:opacity-60 disabled:cursor-not-allowed'
                                >
                                    {isApproving ? 'Approving...' : 'Confirm Approval'}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div
                        onClick={() => !isApproving && setShowApproveModal(false)}
                        className='fixed inset-0 z-[99] bg-black/50 backdrop-blur-sm'
                    />
                </>
            )}

            {/* Reject Modal */}
            {showRejectModal && (
                <>
                    <div className='fixed inset-0 z-[100] flex items-center justify-center p-20'>
                        <div className='w-full max-w-[450px] bg-white rounded-xl shadow-2xl overflow-hidden'>
                            {/* Header */}
                            <div className='bg-danger px-20 py-15 flex items-center justify-between'>
                                <h3 className='text-lg font-semibold text-white flex items-center gap-8'>
                                    <IconAlertTriangle size={20} />
                                    Reject Timesheet
                                </h3>
                                <button
                                    onClick={() => setShowRejectModal(false)}
                                    disabled={rejectTimesheet.isPending}
                                    className='p-4 hover:bg-white/20 rounded-full disabled:opacity-50'
                                >
                                    <IconX size={20} className='text-white' />
                                </button>
                            </div>

                            {/* Body */}
                            <div className='p-20'>
                                {/* Employee info */}
                                <div className='bg-gray-50 border border-border rounded-lg p-15 mb-20'>
                                    <div className='flex items-center gap-12 mb-10'>
                                        <div
                                            style={{ backgroundColor: avatarBg }}
                                            className='w-[40px] h-[40px] rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0'
                                        >
                                            {employeeInitials}
                                        </div>
                                        <div>
                                            <p className='font-semibold text-gray-800'>{employeeName}</p>
                                        </div>
                                    </div>
                                    <div className='flex gap-20 text-sm text-gray-600'>
                                        <span><span className='font-medium'>Period:</span> {getMonthName(tsMonth)} {tsYear}</span>
                                        <span><span className='font-medium'>Hours:</span> {timesheetData?.total_hours || calculateGrandTotal().toFixed(1)}h</span>
                                    </div>
                                </div>

                                <label className='text-sm font-medium text-gray-700 mb-6 block'>
                                    Reason for rejection <span className='text-danger'>*</span>
                                </label>
                                <textarea
                                    className='w-full border border-border rounded-lg p-10 text-sm focus:border-danger outline-none resize-none'
                                    rows={3}
                                    placeholder="Provide a clear reason so the employee can make corrections..."
                                    value={rejectReason}
                                    onChange={e => setRejectReason(e.target.value)}
                                    disabled={rejectTimesheet.isPending}
                                />
                            </div>

                            {/* Footer */}
                            <div className='px-20 py-15 border-t border-border flex justify-end gap-10'>
                                <button
                                    onClick={() => setShowRejectModal(false)}
                                    disabled={rejectTimesheet.isPending}
                                    className='btn btn-outline-secondary disabled:opacity-50'
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmReject}
                                    disabled={rejectTimesheet.isPending}
                                    className='btn btn-danger disabled:opacity-60 disabled:cursor-not-allowed'
                                >
                                    {rejectTimesheet.isPending ? 'Rejecting...' : 'Reject Timesheet'}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div
                        onClick={() => !rejectTimesheet.isPending && setShowRejectModal(false)}
                        className='fixed inset-0 z-[99] bg-black/50 backdrop-blur-sm'
                    />
                </>
            )}
        </div>
    );
}
